/*
 * Os tópicos sugeridos no fim do tópico: as abas "Sugestões" e
 * "✨ Relacionado(a)" no lugar do título, numa tabela compacta com respostas,
 * visualizações e atividade. Com uma lista só, ela ganha o título.
 *
 * - Sugestões: os mais recentes da mesma categoria (ou do fórum, sem tags).
 * - Relacionado(a): os tópicos semânticos do recurso de IA (embeddings),
 *   quando ligado; senão, os que dividem alguma etiqueta com este, os mais
 *   comentados primeiro. Sem nenhum, a aba some.
 *
 * As classes são próprias (.CanelaSuggested*): a lista principal tem outro
 * dono e outro ritmo de mudança, e as duas não devem quebrar juntas.
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import classList from 'flarum/common/utils/classList';
import { categoryBadge, discussionBadges, isTagsEnabled, labelBadge, splitDiscussionTags } from '../../utils/tags';
import { fullDate, shortAge } from '../../utils/dates';
import { forumBool, forumInt, t } from '../../utils/settings';
import { viewsHeat, viewsNumber } from '../../utils/views';
import { aiFeature, aiRequest } from '../../features/ai/state';
import { takePreloaded } from '../../utils/preload';

/** Uma linha da tabela, venha de um modelo do store ou da API de IA. */

/** O que a API de IA devolve em /related/{id}. */

/**
 * Listas já buscadas, por tópico: a linha do fim aparece e some conforme o
 * stream carrega posts, e cada vez seria uma requisição nova.
 */
const cache = new Map();
function include() {
  return isTagsEnabled() ? 'tags,user,lastPostedUser' : 'user,lastPostedUser';
}
function fromDiscussion(topic) {
  const unread = !!app.session.user && topic.isUnread() ? topic.unreadCount() : 0;
  const views = topic.attribute('viewCount');
  return {
    id: topic.id(),
    title: topic.title(),
    href: app.route.discussion(topic, unread ? (topic.lastReadPostNumber() || 0) + 1 : undefined),
    replies: topic.replyCount(),
    views: typeof views === 'number' ? views : null,
    last: topic.lastPostedAt() || topic.createdAt() || null,
    unread,
    badges: discussionBadges(topic)
  };
}
function fromAi(topic) {
  const tags = topic.tagIds.map(id => app.store.getById('tags', id)).filter(Boolean);
  const known = app.store.getById('discussions', topic.id);
  const views = known ? known.attribute('viewCount') : null;
  return {
    id: topic.id,
    title: topic.title,
    href: topic.url,
    replies: topic.replyCount,
    views: typeof views === 'number' ? views : null,
    last: topic.lastPostedAt ? new Date(topic.lastPostedAt) : null,
    unread: 0,
    badges: tags.length ? m("span", {
      className: "CanelaBadges"
    }, tags.map(tag => tag.position() !== null ? categoryBadge(tag) : labelBadge(tag))) : null
  };
}
export default class SuggestedTopics extends Component {
  loading = false;
  tab = 'suggested';
  oninit(vnode) {
    super.oninit(vnode);
    if (!cache.has(this.attrs.discussion.id())) this.load();
  }
  load() {
    const discussion = this.attrs.discussion;
    const id = discussion.id();
    const limit = forumInt('canelaSuggestedCount', 5);

    // Nas discussões curtas o servidor já mandou as listas com a página
    // (src/Preload): a tabela sai no primeiro desenho, sem esperar a rede.
    const preSuggested = this.preloadedSuggested(discussion, limit);
    const preRelated = this.preloadedRelated(discussion, limit);
    if (preSuggested && preRelated) {
      cache.set(id, this.lists(preSuggested, preRelated));
      return;
    }
    this.loading = true;
    Promise.all([preSuggested ?? this.loadSuggested(discussion, limit), preRelated ?? this.loadRelated(discussion, limit)]).then(([suggested, related]) => cache.set(id, this.lists(suggested, related))).catch(() => cache.set(id, {
      suggested: [],
      related: []
    })).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }

  /** As duas listas, sem repetir em "Relacionado(a)" o que já está em "Sugestões". */
  lists(suggested, related) {
    const seen = new Set(suggested.map(row => row.id));
    return {
      suggested,
      related: related.filter(row => !seen.has(row.id))
    };
  }

  /** O pedido de "Sugestões" (o servidor pré-carrega o mesmo: os parâmetros são a chave). */
  suggestedParams(discussion, limit) {
    const {
      categories
    } = splitDiscussionTags(discussion);
    // A mais específica: a filha, quando o tópico está numa subcategoria.
    const category = categories[categories.length - 1];
    const params = {
      sort: '-lastPostedAt',
      page: {
        limit: limit + 1
      },
      include: include()
    };
    if (category) params.filter = {
      tag: category.slug()
    };
    return params;
  }

  /** O pedido de "Relacionado(a)" sem IA; null sem etiquetas. Os slugs em ordem alfabética, como no servidor. */
  relatedParams(discussion, limit) {
    const {
      labels
    } = splitDiscussionTags(discussion);
    if (!labels.length) return null;
    const slugs = labels.map(tag => tag.slug()).sort();
    return {
      sort: '-commentCount',
      filter: {
        tag: slugs.join(',')
      },
      page: {
        limit: limit * 2
      },
      include: include()
    };
  }
  rowsOf(discussion, results, limit) {
    return Array.from(results).filter(item => item && item.id() !== discussion.id()).slice(0, limit).map(fromDiscussion);
  }
  preloadedSuggested(discussion, limit) {
    const payload = takePreloaded('discussions', this.suggestedParams(discussion, limit));
    return payload ? this.rowsOf(discussion, app.store.pushPayload(payload), limit) : null;
  }
  preloadedRelated(discussion, limit) {
    if (aiFeature('related')) {
      const payload = takePreloaded(`canela/ai/related/${discussion.id()}`);
      return payload ? (payload.data || []).slice(0, limit).map(fromAi) : null;
    }
    const params = this.relatedParams(discussion, limit);

    // Sem etiquetas não há o que buscar: a lista vazia já é a resposta.
    if (!params) return [];
    const payload = takePreloaded('discussions', params);
    return payload ? this.rowsOf(discussion, app.store.pushPayload(payload), limit) : null;
  }
  loadSuggested(discussion, limit) {
    return app.store.find('discussions', this.suggestedParams(discussion, limit), undefined, {
      errorHandler: () => {}
    }).then(results => this.rowsOf(discussion, results, limit)).catch(() => []);
  }
  loadRelated(discussion, limit) {
    if (aiFeature('related')) {
      return aiRequest('GET', `related/${discussion.id()}`).then(response => (response.data || []).slice(0, limit).map(fromAi)).catch(() => []);
    }

    // Sem IA: os que dividem uma etiqueta (o filtro do tags junta por "ou").
    const params = this.relatedParams(discussion, limit);
    if (!params) return Promise.resolve([]);
    return app.store.find('discussions', params, undefined, {
      errorHandler: () => {}
    }).then(results => this.rowsOf(discussion, results, limit)).catch(() => []);
  }
  view() {
    if (this.loading) {
      return m("section", {
        className: "CanelaSuggested"
      }, m(LoadingIndicator, {
        display: "block"
      }));
    }
    const lists = cache.get(this.attrs.discussion.id()) || {
      suggested: [],
      related: []
    };
    const tabs = ['suggested', 'related'].filter(tab => lists[tab].length);
    if (!tabs.length) return null;
    const tab = tabs.indexOf(this.tab) !== -1 ? this.tab : tabs[0];
    const rows = lists[tab];
    const views = rows.some(row => row.views !== null);
    const single = tabs.length === 1;
    return m("section", {
      className: classList('CanelaSuggested', {
        'CanelaSuggested--tabs': !single
      }),
      "aria-labelledby": "canela-suggested-title"
    }, single && m("h2", {
      className: "CanelaSuggested-title",
      id: "canela-suggested-title"
    }, tab === 'related' && m(Icon, {
      name: "fas fa-wand-magic-sparkles"
    }), " ", t(`topic.suggested.${tab === 'related' ? 'related_title' : 'title'}`)), m("table", {
      className: "CanelaSuggested-table"
    }, m("thead", null, m("tr", null, m("th", {
      scope: "col",
      className: "CanelaSuggested-topicHead"
    }, single ? t('topic.suggested.topic') : m("ul", {
      className: "CanelaSuggested-tabs",
      role: "tablist",
      id: "canela-suggested-title"
    }, tabs.map(key => m("li", null, m("button", {
      type: "button",
      role: "tab",
      "aria-selected": key === tab ? 'true' : 'false',
      className: classList('CanelaSuggested-tab', {
        active: key === tab
      }),
      onclick: () => this.tab = key
    }, key === 'related' && m(Icon, {
      name: "fas fa-wand-magic-sparkles"
    }), t(`topic.suggested.tab_${key}`)))))), m("th", {
      scope: "col",
      className: "CanelaSuggested-num"
    }, t('topic.suggested.replies')), views && m("th", {
      scope: "col",
      className: "CanelaSuggested-num"
    }, t('topic.suggested.views')), m("th", {
      scope: "col",
      className: "CanelaSuggested-num"
    }, t('topic.suggested.activity')))), m("tbody", null, rows.map(row => this.row(row, views)))));
  }
  row(row, views) {
    return m("tr", {
      className: classList('CanelaSuggested-row', {
        'CanelaSuggested-row--unread': row.unread > 0
      })
    }, m("td", {
      className: "CanelaSuggested-topic"
    }, m(Link, {
      className: "CanelaSuggested-link",
      href: row.href
    }, row.title), row.unread > 0 && m("span", {
      className: "CanelaSuggested-unread"
    }, abbreviateNumber(row.unread)), m("div", {
      className: "CanelaSuggested-badges"
    }, row.badges)), m("td", {
      className: "CanelaSuggested-num CanelaSuggested-replies"
    }, abbreviateNumber(row.replies)), views && this.viewsCell(row.views || 0), m("td", {
      className: "CanelaSuggested-num CanelaSuggested-activity",
      title: row.last ? fullDate(row.last) : ''
    }, row.last ? shortAge(row.last) : ''));
  }

  /** Como a coluna da lista: o número curto, o title por extenso e o calor (`heatmap-*`). */
  viewsCell(count) {
    const heat = forumBool('canelaListHeat', true) ? viewsHeat(count) : null;
    return m("td", {
      className: classList('CanelaSuggested-num CanelaSuggested-views', heat && `heatmap-${heat}`)
    }, viewsNumber(count));
  }
}
flarum.reg.add('ramon-canela', 'forum/topic/components/SuggestedTopics', SuggestedTopics);